import {
  Box, VStack, HStack, Text, Input, Button, Switch, IconButton
} from '@chakra-ui/react';
import type { ShowcaseCfg } from './types';

type FieldKey = 'brandName' | 'handle' | 'logoUrl' | 'domain' | 'cta' | 'headline' | 'primary' | 'description';

const IDENTITY_FIELDS: Array<{ key: FieldKey; label: string; badge: string; placeholder: string }> = [
  { key: 'brandName', label: 'Brand / page name', badge: 'Brand',      placeholder: 'Brand name' },
  { key: 'handle',    label: 'Instagram handle',  badge: 'Instagram',  placeholder: 'handle' },
  { key: 'logoUrl',   label: 'Logo URL',          badge: 'Brand',      placeholder: 'https://…' },
  { key: 'domain',    label: 'Display domain',    badge: 'Brand',      placeholder: 'example.com' }
];

const COPY_FIELDS: Array<{ key: FieldKey; label: string; badge: string; placeholder: string }> = [
  { key: 'cta',         label: 'CTA label',                 badge: 'Default', placeholder: 'Shop now' },
  { key: 'headline',    label: 'Headline override',         badge: 'Per ad',  placeholder: 'From the ad' },
  { key: 'primary',     label: 'Primary text override',     badge: 'Per ad',  placeholder: 'From the ad' },
  { key: 'description', label: 'Description override',      badge: 'Per ad',  placeholder: 'From the ad' }
];

type Props = {
  cfg: ShowcaseCfg;
  recommended: ShowcaseCfg;
  onCfgChange: (patch: Partial<ShowcaseCfg>) => void;
  onClose: () => void;
  surround: 'dark' | 'light';
};

function fieldValue(cfg: ShowcaseCfg, key: FieldKey): string {
  const v = cfg[key];
  return v == null ? '' : String(v);
}

export function ConfigureChromePanel({ cfg, recommended, onCfgChange, onClose, surround }: Props) {
  const dark = surround === 'dark';
  const panel = dark ? '#12141A' : 'white';
  const fg = dark ? '#F5F7FA' : 'brand.ink';
  const fg2 = dark ? '#A8B0BE' : 'brand.muted';
  const fg3 = dark ? '#6C7484' : 'gray.500';
  const bd = dark ? 'whiteAlpha.200' : 'brand.border';
  const inputBg = dark ? '#171A21' : 'white';

  function renderField(f: { key: FieldKey; label: string; badge: string; placeholder: string }) {
    const value = fieldValue(cfg, f.key);
    const rec = fieldValue(recommended, f.key);
    const dirty = value !== rec;
    const perAd = f.badge === 'Per ad';
    return (
      <Box key={f.key} as="label" display="block">
        <HStack spacing={2} mb={1.5}>
          <Text fontSize="12px" fontWeight="500" color={fg2}>{f.label}</Text>
          <Text
            as="span"
            fontSize="9.5px"
            fontWeight="700"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color={perAd ? fg3 : 'rsCyan.500'}
            bg={perAd ? (dark ? 'whiteAlpha.100' : 'gray.100') : 'rgba(22,184,216,0.14)'}
            borderRadius="4px"
            px="5px"
            py="2px"
          >
            {f.badge}
          </Text>
          {dirty && (
            <Button
              variant="link"
              size="xs"
              color="rsCyan.500"
              ml="auto"
              h="auto"
              minW="auto"
              fontSize="11px"
              fontWeight="500"
              onClick={() => onCfgChange({ [f.key]: recommended[f.key] })}
            >
              Reset
            </Button>
          )}
        </HStack>
        <Input
          value={value}
          placeholder={f.placeholder}
          onChange={e => onCfgChange({ [f.key]: e.target.value })}
          size="sm"
          bg={inputBg}
          color={fg}
          borderColor={bd}
          _placeholder={{ color: fg3 }}
        />
      </Box>
    );
  }

  const sponsoredOn = cfg.showSponsored !== false;
  const engagementOn = cfg.showEngagement !== false;

  return (
    <Box
      position="fixed"
      top={0}
      right={0}
      bottom={0}
      w="392px"
      maxW="100vw"
      zIndex={1200}
      bg={panel}
      borderLeftWidth="1px"
      borderColor={bd}
      display="flex"
      flexDirection="column"
      boxShadow={dark ? '-24px 0 60px rgba(0,0,0,.35)' : 'lg'}
      color={fg}
    >
      <HStack spacing={3} px={5} py={5} borderBottomWidth="1px" borderColor={bd}>
        <Box>
          <Text fontSize="13.5px" fontWeight="600" letterSpacing="-0.01em">Chrome configuration</Text>
          <Text mt="3px" fontSize="11px" color={fg3}>Identity and copy defaults for this showcase</Text>
        </Box>
        <IconButton
          aria-label="Close"
          ml="auto"
          size="sm"
          variant="outline"
          borderColor={bd}
          color={fg2}
          onClick={onClose}
          icon={<CloseGlyph />}
        />
      </HStack>
      <Box flex={1} overflowY="auto" px={5} pb={7}>
        <Box pt={5}>
          <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.11em" textTransform="uppercase" color={fg3} mb={3}>
            Identity
          </Text>
          <VStack align="stretch" spacing={3.5}>
            {IDENTITY_FIELDS.map(renderField)}
          </VStack>
        </Box>
        <Box pt={6}>
          <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.11em" textTransform="uppercase" color={fg3} mb={3}>
            Copy defaults
          </Text>
          <VStack align="stretch" spacing={3.5}>
            {COPY_FIELDS.map(renderField)}
          </VStack>
        </Box>
        <Box pt={6}>
          <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.11em" textTransform="uppercase" color={fg3} mb={3}>
            Display
          </Text>
          <VStack align="stretch" spacing={1}>
            <ToggleRow
              label="Sponsored label"
              hint="Show the platform disclosure row"
              isOn={sponsoredOn}
              onToggle={() => onCfgChange({ showSponsored: !sponsoredOn })}
              fg={fg}
              fg3={fg3}
            />
            <ToggleRow
              label="Engagement + caption"
              hint="Generic like counts and caption body"
              isOn={engagementOn}
              onToggle={() => onCfgChange({ showEngagement: !engagementOn })}
              fg={fg}
              fg3={fg3}
            />
          </VStack>
        </Box>
        <Button
          mt={6}
          w="full"
          variant="outline"
          size="sm"
          borderColor={bd}
          color={fg2}
          onClick={() => onCfgChange({ ...recommended })}
        >
          Reset all to recommended
        </Button>
      </Box>
    </Box>
  );
}

function ToggleRow({
  label, hint, isOn, onToggle, fg, fg3
}: {
  label: string;
  hint: string;
  isOn: boolean;
  onToggle: () => void;
  fg: string;
  fg3: string;
}) {
  return (
    <HStack
      as="button"
      spacing={3}
      w="full"
      py={2}
      px={1.5}
      textAlign="left"
      onClick={onToggle}
      _hover={{ bg: 'whiteAlpha.50' }}
      borderRadius="md"
    >
      <Box flex={1} minW={0}>
        <Text fontSize="12.5px" fontWeight="500" color={fg}>{label}</Text>
        <Text mt="2px" fontSize="11px" color={fg3}>{hint}</Text>
      </Box>
      <Switch
        isChecked={isOn}
        onChange={onToggle}
        colorScheme="purple"
        size="md"
        onClick={e => e.stopPropagation()}
      />
    </HStack>
  );
}

function CloseGlyph() {
  return (
    <Box as="span" w="14px" h="14px" display="block">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M5 5l14 14M19 5L5 19" />
      </svg>
    </Box>
  );
}
